import { HighLevelContextState, HighLevelAction } from "./types";

export const highlevelContextReducer = (
  state: HighLevelContextState,
  action: HighLevelAction
): HighLevelContextState => {
  switch (action.type) {
    case "SET_CLIENT":
      return { 
        ...state, 
        client: action.payload 
      };
    
    case "SET_LOADING":
      return { 
        ...state, 
        isLoading: action.payload 
      };
    
    case "SET_ERROR":
      return { 
        ...state, 
        error: action.payload,
        isLoading: false 
      };
    
    case "CLEAR_ERROR":
      return { 
        ...state, 
        error: null 
      };
    
    default:
      return state;
  }
};

/**
 * Helper function to transform HighLevel billing cycle to display format
 */
export const formatBillingPeriod = (period: string): string => {
  switch (period) {
    case 'monthly':
      return 'Billed Monthly';
    case 'annually':
      return 'Billed Annually';
    case 'bi-annually':
      return 'Billed Bi-Annually';
    default:
      return period;
  }
};

/**
 * Helper function to parse HighLevel product name to extract tier
 */
export const extractTierFromName = (name: string): 'home' | 'design' | 'designPlus' => {
  const lowercaseName = name.toLowerCase();
  
  if (lowercaseName.includes('design plus') || lowercaseName.includes('design+')) {
    return 'designPlus';
  } else if (lowercaseName.includes('design')) {
    return 'design';
  }
  
  return 'home';
};

/**
 * Helper function to parse HighLevel product name to extract billing cycle
 */
export const extractBillingFromName = (name: string): 'monthly' | 'annually' | 'bi-annually' => {
  const lowercaseName = name.toLowerCase();
  
  if (lowercaseName.includes('bi-annual')) {
    return 'bi-annually';
  } else if (lowercaseName.includes('annual') || lowercaseName.includes('yearly')) {
    return 'annually';
  }
  
  return 'monthly';
};

/**
 * Helper function to format price for display
 */
export const formatPrice = (price: number, currency: string = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
};

/**
 * Map of HighLevel product IDs to your membership tiers
 * Update these with your actual HighLevel product IDs
 */
export const HIGHLEVEL_PRODUCT_MAP = {
  home: {
    monthly: process.env.REACT_APP_HL_HOME_MONTHLY_ID || 'prod_home_monthly',
    annually: process.env.REACT_APP_HL_HOME_ANNUAL_ID || 'prod_home_annual',
    'bi-annually': process.env.REACT_APP_HL_HOME_BIANNUAL_ID || 'prod_home_biannual'
  },
  design: {
    monthly: process.env.REACT_APP_HL_DESIGN_MONTHLY_ID || 'prod_design_monthly',
    annually: process.env.REACT_APP_HL_DESIGN_ANNUAL_ID || 'prod_design_annual',
    'bi-annually': process.env.REACT_APP_HL_DESIGN_BIANNUAL_ID || 'prod_design_biannual'
  },
  designPlus: {
    monthly: process.env.REACT_APP_HL_DESIGNPLUS_MONTHLY_ID || 'prod_designplus_monthly',
    annually: process.env.REACT_APP_HL_DESIGNPLUS_ANNUAL_ID || 'prod_designplus_annual',
    'bi-annually': process.env.REACT_APP_HL_DESIGNPLUS_BIANNUAL_ID || 'prod_designplus_biannual'
  }
};

/**
 * Validate HighLevel API response
 */
export const validateHighLevelResponse = (response: any): boolean => {
  if (!response || typeof response !== 'object') {
    return false;
  }
  
  // Add more validation as needed based on HighLevel's response structure
  return true;
};

/**
 * Error messages for common HighLevel API errors
 */
export const getErrorMessage = (error: any): string => {
  if (error?.response?.status === 401) {
    return 'Authentication failed. Please check your HighLevel API key.';
  } else if (error?.response?.status === 404) {
    return 'Resource not found. Please check the product ID.';
  } else if (error?.response?.status === 429) {
    return 'Rate limit exceeded. Please try again later.';
  } else if (error?.message) {
    return error.message;
  }
  
  return 'An unexpected error occurred. Please try again.';
};
